import React, { useState, useEffect, useCallback } from 'react';
import { Button, Card, Tag, Spin, Empty, Typography, Space, Divider, Grid, Tooltip } from 'antd';
import {
  GoogleOutlined, ArrowLeftOutlined, ShopOutlined, EnvironmentOutlined, ShareAltOutlined,
} from '@ant-design/icons';
import { useParams, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import { encodeInsight } from '../utils/insightCode';

const { Title, Text } = Typography;
const { useBreakpoint } = Grid;

const API_BASE = process.env.REACT_APP_API_BASE || '';

const BRAND = 'var(--brand-primary, #222957)';
const BORDER = '#E5E7F5';
const HEADING = '#222A5A';

const getToken = () => {
  try { return localStorage.getItem('customer_token'); } catch { return null; }
};

// 訂單進度：下單 → 付款 → 製作中 → 顧客確認 → 出貨 → 收貨
const ORDER_STAGES = [
  { key: 'ordered', label: '下單', desc: '訂單已成立，我們已收到您的訂單。' },
  { key: 'paid', label: '付款', desc: '完成付款後即進入製作排程（貨到付款則於取貨時付款）。' },
  { key: 'making', label: '製作中', desc: '手作職人正在為您製作專屬手鏈，請耐心等候。' },
  { key: 'confirm', label: '顧客確認', desc: '如需確認款式／設計，我們會與您聯繫，確認後安排出貨。' },
  { key: 'shipped', label: '出貨', desc: '商品已出貨，可於下方物流資訊查看單號與配送進度。' },
  { key: 'received', label: '收貨', desc: '您已完成取貨／收貨，感謝購買！有問題歡迎聯繫客服。' },
];

// 依訂單資料推算目前進度（回傳「進行中」的階段索引；已完成的階段索引皆小於它）
function currentStageIndex(order) {
  const ls = String(order.logistics_status || '');
  const paid = order.payment_status === 'paid' || order.payment_status === 'cod';
  const delivered = order.status === 'completed' || /送達|已取貨|已領|完成/.test(ls);
  const shipped = delivered || !!order.logistics_no || /出貨|配送|到店|已到|取貨/.test(ls);
  if (delivered) return 6;                         // 全部完成
  if (shipped) return 4;                           // 出貨（待收貨）
  if (order.customer_confirmed_at) return 4;       // 已確認，待出貨
  if (order.proof_sent_at) return 3;               // 待顧客確認
  if (paid) return 2;                              // 製作中
  return 1;                                        // 待付款
}

function OrderProgress({ order, isMobile }) {
  const current = currentStageIndex(order);
  return (
    <Card style={{ marginBottom: 12, borderRadius: 16, borderColor: BORDER }} bodyStyle={{ padding: isMobile ? '16px 10px' : 16 }}>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 14 }}>訂單進度</Text>
      <div style={{ overflowX: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', minWidth: 336 }}>
          {ORDER_STAGES.map((s, i) => {
            const done = i < current;
            const active = i === current;
            return (
              <Tooltip key={s.key} title={s.desc} placement="top">
                <div style={{ flex: 1, position: 'relative', textAlign: 'center', minWidth: 0, cursor: 'help' }}>
                  {i > 0 && (
                    <div style={{ position: 'absolute', top: 15, left: '-50%', width: '100%', height: 3, background: i <= current ? BRAND : '#E5E7F5', zIndex: 0 }} />
                  )}
                  <div style={{
                    position: 'relative', zIndex: 1, width: 32, height: 32, margin: '0 auto', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 700,
                    background: done ? BRAND : active ? '#fff' : '#EDEFF8',
                    color: done ? '#fff' : active ? BRAND : '#BCC1E5',
                    border: (done || active) ? `2px solid ${BRAND}` : '2px solid #E5E7F5',
                    boxShadow: active ? '0 0 0 4px rgba(34,41,87,0.14)' : 'none',
                  }}>
                    {done ? '✓' : i + 1}
                  </div>
                  <div style={{ marginTop: 6, fontSize: isMobile ? 11 : 12, fontWeight: (done || active) ? 700 : 500, color: (done || active) ? HEADING : '#BCC1E5', whiteSpace: 'nowrap' }}>
                    {s.label}
                  </div>
                </div>
              </Tooltip>
            );
          })}
        </div>
      </div>
    </Card>
  );
}

// 付款狀態 → 標籤
const paymentTag = (status) => {
  if (status === 'paid') return <Tag color="green">已付款</Tag>;
  if (status === 'cod') return <Tag color="blue">貨到付款</Tag>;
  if (status === 'failed') return <Tag color="red">付款失敗</Tag>;
  if (status === 'pending') return <Tag color="orange">未付款</Tag>;
  return <Tag>未付款</Tag>;
};

// 訂單處理狀態
const orderStatusLabel = (status) => {
  if (status === 'processing') return '處理中';
  if (status === 'completed') return '已完成';
  if (status === 'cancelled') return '已取消';
  return '待處理';
};

const paymentMethodLabel = (m) => {
  if (m === 'ecpay') return '綠界線上付款';
  if (m === 'cod') return '貨到付款';
  return m || '—';
};

const chainStyleLabel = (v) => {
  if (v === 'elastic') return '彈力繩';
  if (v === 'adjustable') return '可調節';
  return v || null;
};

const circleDesignLabel = (v) => {
  if (v === 'single') return '單圈';
  if (v === 'double') return '雙圈';
  return v || null;
};

const logisticsTypeLabel = (v) => {
  if (v === 'cvs') return '超商取貨';
  if (v === 'home') return '宅配到府';
  return v || null;
};

// 客製選色方式
const colorMethodLabel = (v) => {
  if (v === 'wuxing') return '五行';
  if (v === 'numerology') return '生命靈數';
  if (v === 'effect') return '功效';
  if (v === 'intuitive') return '直覺';
  return v || null;
};

// 客製鏈形（含磁扣款）
const customChainStyleLabel = (v) => {
  if (v === 'elastic') return '彈性繩';
  if (v === 'adjustable') return '調節鍊';
  if (v === 'magnetic') return '磁扣款';
  return v || null;
};

// 客製設計明細分析摘要（依 color_method 取對應結果）
const analysisSummary = (d) => {
  if (!d) return null;
  if (d.color_method === 'wuxing') {
    const w = d.wuxing_result;
    if (!w) return null;
    if (typeof w === 'string') return w;
    const a = w.analysis || {};
    const parts = [];
    if (Array.isArray(a.strongest) && a.strongest.length) parts.push(`最強 ${a.strongest.join('、')}`);
    if (Array.isArray(a.weakest) && a.weakest.length) parts.push(`最弱 ${a.weakest.join('、')}`);
    return parts.length ? parts.join('，') : null;
  }
  if (d.color_method === 'numerology') {
    const n = d.numerology_result;
    if (!n) return null;
    if (typeof n === 'string') return n;
    const lp = n.coreNumbers?.lifePath;
    if (lp) return `命數 ${lp.value}${lp.summary ? ` — ${lp.summary}` : ''}`;
    return null;
  }
  if (d.color_method === 'effect') return d.effect_choice || null;
  if (d.color_method === 'intuitive') return d.intuitive_color || null;
  return d.color_strategy || d.color_result || null;
};

// 訂購內容摘要
const itemSummary = (o) => {
  if (o.selected_bracelet_name) return o.selected_bracelet_name;
  if (Array.isArray(o.selected_crystals) && o.selected_crystals.length > 0) {
    const names = o.selected_crystals.map((c) => c.crystal_name).filter(Boolean);
    if (names.length > 0) return `客製手鏈 · ${names.join('、')}`;
    return '客製手鏈';
  }
  if (Array.isArray(o.selected_colors) && o.selected_colors.length > 0) {
    return `客製手鏈 · ${o.selected_colors.join('、')}`;
  }
  return '客製手鏈';
};

export default function OrderDetail() {
  const { orderCode } = useParams();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const [checkingAuth, setCheckingAuth] = useState(true);
  const [loggedIn, setLoggedIn] = useState(false);
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]); // 多品項訂單品項
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // 驗證登入
  useEffect(() => {
    const token = getToken();
    if (!token) { setCheckingAuth(false); return; }
    fetch(`${API_BASE}/api/auth/google/customer/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((info) => {
        if (info) setLoggedIn(true);
        else { try { localStorage.removeItem('customer_token'); } catch {} }
      })
      .catch(() => {})
      .finally(() => setCheckingAuth(false));
  }, []);

  // 取得訂單清單並找出符合 orderCode 者
  const loadOrder = useCallback(() => {
    const token = getToken();
    if (!token) return;
    setLoading(true);
    setNotFound(false);
    fetch(`${API_BASE}/api/bracelet-orders/my`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : { items: [] }))
      .then((d) => {
        const found = (d.items || []).find((o) => o.order_code === orderCode);
        if (found) setOrder(found);
        else setNotFound(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [orderCode]);

  // 多品項訂單：另抓品項清單（單一設計款/客製單則回無品項，優雅退回既有顯示）
  const loadItems = useCallback(() => {
    const token = getToken();
    if (!token) return;
    fetch(`${API_BASE}/api/orders/my/${orderCode}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setItems((d && Array.isArray(d.items)) ? d.items : []))
      .catch(() => setItems([]));
  }, [orderCode]);

  useEffect(() => {
    if (loggedIn) { loadOrder(); loadItems(); }
  }, [loggedIn, loadOrder, loadItems]);

  const startCustomerLogin = useCallback(() => {
    window.location.href = `${API_BASE}/api/auth/google/customer/login?next=${encodeURIComponent(`/orders/${orderCode}`)}`;
  }, [orderCode]);

  // 未付款訂單：重新導向綠界付款頁
  const repay = useCallback(() => {
    if (order) window.location.href = `${API_BASE}/api/payments/ecpay/checkout/${order.id}?ctoken=${encodeURIComponent(localStorage.getItem('customer_token') || '')}`;
  }, [order]);

  const containerStyle = {
    maxWidth: 720,
    margin: '0 auto',
    padding: isMobile ? '16px' : '24px',
    position: 'relative',
    zIndex: 1,
  };

  const rowStyle = { display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 8 };

  const detailRow = (label, value, opts = {}) => {
    if (value == null || value === '') return null;
    return (
      <div style={rowStyle}>
        <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>{label}</Text>
        {opts.node ? value : (
          <Text
            copyable={opts.copyable}
            strong={opts.strong}
            style={{ textAlign: 'right', color: opts.strong ? 'var(--brand-primary)' : undefined }}
          >
            {value}
          </Text>
        )}
      </div>
    );
  };

  // 客製手鏈設計明細（顯示於品項下方）
  const renderCustomDetail = (d) => {
    if (!d) return null;
    const crystalNames = Array.isArray(d.selected_crystals)
      ? d.selected_crystals.map((c) => c && (c.crystal_name || c.name)).filter(Boolean)
      : [];
    const colors = Array.isArray(d.selected_colors) ? d.selected_colors.filter(Boolean) : [];
    const summary = analysisSummary(d);
    const dRow = (label, value) => {
      if (value == null || value === '') return null;
      return (
        <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
          <Text type="secondary" style={{ whiteSpace: 'nowrap', fontSize: 12, flex: '0 0 auto' }}>{label}</Text>
          <div style={{ flex: 1, fontSize: 12 }}>{value}</div>
        </div>
      );
    };
    return (
      <div
        style={{
          marginTop: 8,
          padding: '10px 12px',
          borderRadius: 10,
          background: '#F9FAFD',
          borderLeft: `3px solid ${BRAND}`,
        }}
      >
        <Text strong style={{ display: 'block', color: BRAND, fontSize: 12, marginBottom: 4 }}>
          客製設計明細
        </Text>
        {dRow('選色方式', colorMethodLabel(d.color_method))}
        {dRow('分析結果', summary)}
        {colors.length > 0 && dRow('選擇顏色', (
          <Space size={4} wrap>
            {colors.map((c) => <Tag key={c} style={{ marginInlineEnd: 0 }}>{c}</Tag>)}
          </Space>
        ))}
        {crystalNames.length > 0 && dRow('專屬水晶', crystalNames.join('、'))}
        {dRow('鏈形', customChainStyleLabel(d.chain_style))}
        {dRow('圈數', circleDesignLabel(d.circle_design))}
        {dRow('手圍', d.wrist_size != null && d.wrist_size !== '' ? `${d.wrist_size} cm` : null)}
        {dRow('特殊需求', d.special_requirements)}
        {d.birthday && (d.color_method === 'wuxing' || d.color_method === 'numerology') && (
          <Button
            size="small"
            icon={<ShareAltOutlined />}
            style={{ marginTop: 10 }}
            onClick={() => window.open(
              `/insight?c=${encodeInsight(
                d.color_method === 'wuxing'
                  ? { type: 'wuxing', d: d.birthday, t: d.birth_time }
                  : { type: 'numerology', d: d.birthday },
              )}`,
              '_blank',
            )}
          >
            查看完整{d.color_method === 'wuxing' ? '五行' : '生命靈數'}分析 · 可分享收藏
          </Button>
        )}
      </div>
    );
  };

  const backLink = (
    <Link to="/orders" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--brand-primary)' }}>
      <ArrowLeftOutlined /> 返回我的訂單
    </Link>
  );

  // 尚在驗證登入
  if (checkingAuth) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  // 未登入
  if (!loggedIn) {
    return (
      <div style={containerStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24, paddingTop: 24 }}>
          <Title level={3} style={{ color: '#222A5A', fontWeight: 800 }}>訂單詳情</Title>
          <Text type="secondary">用 Google 登入後，即可查看此訂單</Text>
        </div>
        <div style={{ textAlign: 'center' }}>
          <Button
            type="primary" size="large" icon={<GoogleOutlined />}
            onClick={startCustomerLogin}
            style={{ height: 48, fontSize: 16 }}
          >
            登入查看訂單
          </Button>
        </div>
      </div>
    );
  }

  // 載入中
  if (loading && !order) {
    return (
      <div style={{ ...containerStyle, textAlign: 'center', paddingTop: 80 }}>
        <Spin size="large" />
      </div>
    );
  }

  // 找不到訂單
  if (notFound || !order) {
    return (
      <div style={containerStyle}>
        <div style={{ marginBottom: 16 }}>{backLink}</div>
        <Empty description={`找不到訂單 ${orderCode}`} style={{ padding: '48px 0' }} />
      </div>
    );
  }

  const unpaid = order.payment_status !== 'paid' && order.payment_status !== 'cod';
  const canRepay = unpaid && order.payment_method === 'ecpay';

  return (
    <div style={containerStyle}>
      <div style={{ marginTop: 12, marginBottom: 12 }}>{backLink}</div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, marginBottom: 12 }}>
        <Title level={3} style={{ margin: 0, color: BRAND, fontWeight: 800, letterSpacing: 1 }}>訂單詳情</Title>
        {paymentTag(order.payment_status)}
      </div>

      {/* 訂單進度條（依目前進度呈現；滑鼠移到每個階段有說明） */}
      {order.status === 'cancelled' ? (
        <Card style={{ marginBottom: 12, borderRadius: 16, borderColor: BORDER }} bodyStyle={{ padding: 16 }}>
          <Tag color="red" style={{ margin: 0 }}>此訂單已取消</Tag>
        </Card>
      ) : (
        <OrderProgress order={order} isMobile={isMobile} />
      )}

      {/* 製作完成、待客人確認：顯著 CTA 進成品確認頁 */}
      {order.proof_sent_at && !order.customer_confirmed_at && order.confirm_token && order.status !== 'cancelled' && (
        <Card style={{ marginBottom: 12, borderRadius: 16, borderColor: BRAND, background: '#F4F5FB' }} bodyStyle={{ padding: 16 }}>
          <Text strong style={{ display: 'block', color: HEADING, marginBottom: 6 }}>✨ 您的手鏈已製作完成，請確認成品</Text>
          <Text type="secondary" style={{ fontSize: 13 }}>確認後即安排出貨；如需微調也可在頁面提出。</Text>
          <Link to={`/orders/${order.order_code}/confirm?token=${order.confirm_token}`}>
            <Button type="primary" block style={{ height: 46, borderRadius: 12, marginTop: 12, background: BRAND, borderColor: BRAND, fontWeight: 700 }}>
              查看成品並確認
            </Button>
          </Link>
        </Card>
      )}

      {/* 訂購品項（多品項訂單）；無品項時退回下方客製單摘要 */}
      {items.length > 0 ? (
        <Card style={{ marginBottom: 12, borderRadius: 16, borderColor: BORDER }} bodyStyle={{ padding: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>訂購品項</Text>
          <div style={{ marginTop: 8 }}>
            {items.map((it, i) => (
              <div
                key={it.id || i}
                style={{
                  padding: '8px 0',
                  borderTop: i === 0 ? 'none' : '1px solid #EDEFF8',
                }}
              >
                <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                  {it.image_url && (
                    <img
                      src={it.image_url} alt={it.product_name}
                      style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8, flex: '0 0 auto', background: '#F9FAFD' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  )}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Text strong style={{ display: 'block', color: '#222A5A' }}>{it.product_name}</Text>
                    {it.spec_label && <Text type="secondary" style={{ fontSize: 12 }}>{it.spec_label}</Text>}
                  </div>
                  <Text style={{ whiteSpace: 'nowrap' }}>
                    NT$ {it.unit_price} × {it.quantity}
                  </Text>
                </div>
                {it.is_custom && it.custom_detail && renderCustomDetail(it.custom_detail)}
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card style={{ marginBottom: 12, borderRadius: 16, borderColor: BORDER }} bodyStyle={{ padding: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>訂購內容</Text>
          <div style={{ marginTop: 6 }}>
            <Text strong style={{ color: '#222A5A', fontSize: 16 }}>{itemSummary(order)}</Text>
          </div>
          {detailRow('款式', chainStyleLabel(order.chain_style))}
          {detailRow('圈數', circleDesignLabel(order.circle_design))}
          {detailRow('手圍', order.wrist_size != null ? `${order.wrist_size} cm` : null)}
          {detailRow('特殊需求', order.special_requirements)}
        </Card>
      )}

      {/* 訂單資訊 */}
      <Card style={{ marginBottom: 12, borderRadius: 16, borderColor: BORDER }} bodyStyle={{ padding: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>訂單資訊</Text>
        {detailRow('訂單編號', order.order_code, { copyable: true, strong: true })}
        {detailRow('訂單狀態', <Tag style={{ margin: 0 }}>{orderStatusLabel(order.status)}</Tag>, { node: true })}
        {detailRow('成立時間', order.created_at ? dayjs(order.created_at).format('YYYY-MM-DD HH:mm') : null)}
      </Card>

      {/* 付款資訊 */}
      <Card style={{ marginBottom: 12, borderRadius: 16, borderColor: BORDER }} bodyStyle={{ padding: 16 }}>
        <Text type="secondary" style={{ fontSize: 12 }}>付款資訊</Text>
        {detailRow('付款狀態', paymentTag(order.payment_status), { node: true })}
        {detailRow('付款方式', paymentMethodLabel(order.payment_method))}
        {detailRow('金額', order.amount != null ? `NT$ ${order.amount}` : null, { strong: true })}
        {order.refund_amount != null && order.refund_amount > 0 &&
          detailRow('退款金額', `NT$ ${order.refund_amount}`)}
        {order.paid_at && detailRow('付款時間', dayjs(order.paid_at).format('YYYY-MM-DD HH:mm'))}
        {detailRow('綠界交易編號', order.ecpay_trade_no, { copyable: true })}
      </Card>

      {/* 物流資訊 */}
      {(order.logistics_type || order.cvs_store_name || order.logistics_status || order.logistics_no || order.recipient_name) && (
        <Card style={{ marginBottom: 12, borderRadius: 16, borderColor: BORDER }} bodyStyle={{ padding: 16 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>物流資訊</Text>
          {detailRow('物流方式', logisticsTypeLabel(order.logistics_type))}
          {detailRow(<Space size={4}><ShopOutlined />取貨門市</Space>, order.cvs_store_name)}
          {order.cvs_store_address && detailRow('門市地址', order.cvs_store_address)}
          {order.recipient_name && detailRow('收件人', order.recipient_name)}
          {order.recipient_address && detailRow('收件地址', order.recipient_address)}
          {detailRow(<Space size={4}><EnvironmentOutlined />物流狀態</Space>, order.logistics_status)}
          {detailRow('物流單號', order.logistics_no, { copyable: true })}
        </Card>
      )}

      {canRepay && (
        <Button type="primary" block style={{ height: 48, borderRadius: 12, marginBottom: 12 }} onClick={repay}>
          繼續付款{order.amount != null ? ` NT$ ${order.amount}` : ''}
        </Button>
      )}

      <Divider style={{ margin: '4px 0 16px' }} />
      <div style={{ textAlign: 'center' }}>{backLink}</div>
    </div>
  );
}
